import { useI18n } from '@affine/i18n';
import {
  STICKY_NOTE_COLOR_VALUES,
  STICKY_NOTE_PAPER_PATHS,
  STICKY_NOTE_PIN_COLORS,
} from '@blocksuite/affine-block-streamlain';
import {
  AttachmentIcon,
  DatabaseTableViewIcon,
  DeleteIcon,
  EdgelessIcon,
  FileIconPdfIcon,
  ImageIcon,
  LinkIcon,
  NoNetworkIcon,
  PageIcon,
  PlayIcon,
  TextIcon,
} from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import type { ComponentType } from 'react';

import { WorkspaceService } from '../../workspace';
import { useWorkspaceBlobObjectUrl } from '../../workspace/views/use-workspace-blob-object-url';
import {
  QUICK_SEARCH_HIT_KIND_LABEL,
  type QuickSearchHitKind,
  type QuickSearchHitVisual,
} from '../types/item';
import * as styles from './search-hit-visual.css';

const KIND_ICON: Record<
  Exclude<QuickSearchHitKind, 'sticky' | 'pdf'>,
  ComponentType
> = {
  doc: PageIcon,
  text: TextIcon,
  image: ImageIcon,
  canvas: EdgelessIcon,
  attachment: AttachmentIcon,
  link: LinkIcon,
  database: DatabaseTableViewIcon,
  automation: PlayIcon,
};

const STICKY_COLOR = 'yellow';

function StickyMiniature() {
  return (
    <span className={styles.sticky}>
      <svg
        className={styles.stickyPaper}
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d={STICKY_NOTE_PAPER_PATHS[0]}
          fill={STICKY_NOTE_COLOR_VALUES[STICKY_COLOR]}
        />
      </svg>
      <span className={styles.stickyLines} />
      <span
        className={styles.stickyPin}
        style={{ backgroundColor: STICKY_NOTE_PIN_COLORS[STICKY_COLOR] }}
      />
    </span>
  );
}

function ImageMiniature({ blobId }: { blobId: string }) {
  const workspaceId = useService(WorkspaceService).workspace.id;
  const url = useWorkspaceBlobObjectUrl(workspaceId, blobId);
  return url ? (
    <img className={styles.image} src={url} alt="" draggable={false} />
  ) : (
    <ImageIcon />
  );
}

/** The kind of a hit drawn as what it is: image, PDF, sticky note, tile. */
export function SearchHitVisual({ visual }: { visual: QuickSearchHitVisual }) {
  const i18n = useI18n();
  const { kind, blobId } = visual;
  const Icon = kind === 'sticky' || kind === 'pdf' ? null : KIND_ICON[kind];
  return (
    <span
      className={styles.thumb}
      data-kind={kind}
      data-has-image={kind === 'image' && blobId ? 'true' : undefined}
      role="img"
      aria-label={i18n.t({ i18nKey: QUICK_SEARCH_HIT_KIND_LABEL[kind] })}
      title={i18n.t({ i18nKey: QUICK_SEARCH_HIT_KIND_LABEL[kind] })}
      data-testid="search-hit-visual"
    >
      {kind === 'sticky' ? (
        <StickyMiniature />
      ) : kind === 'pdf' ? (
        <FileIconPdfIcon />
      ) : kind === 'image' && blobId ? (
        <ImageMiniature blobId={blobId} />
      ) : Icon ? (
        <Icon />
      ) : null}
    </span>
  );
}

/** A doc that cannot be opened offline: its text is not on this device. */
export function SearchHitOfflinePill({
  state,
}: {
  state: 'unavailable' | 'pending';
}) {
  const i18n = useI18n();
  return (
    <span className={styles.trashPill} data-testid="search-hit-offline">
      <NoNetworkIcon />
      {i18n.t({ i18nKey: `com.streamlain.quicksearch.offline.${state}` })}
    </span>
  );
}

export function SearchHitTrashPill() {
  const i18n = useI18n();
  return (
    <span className={styles.trashPill} data-testid="search-hit-trashed">
      <DeleteIcon />
      {i18n.t({ i18nKey: 'com.streamlain.quicksearch.trashed' })}
    </span>
  );
}
